/** Turn an ordinary document folder into a project (RFC 0357 S5). */

import type { AnyDocumentId, Repo } from '@automerge/automerge-repo/slim'
import { ensureProjectSources, projectSources } from '../code/sources'
import { mapWithConflicts } from '../comments/store'
import type { CommentIndexEntry } from '../comments/types'
import { moveToProject } from '../documents/move-project'
import { createFolder, normalizeDocumentPath } from '../documents/mutations'
import { projectFolders } from '../documents/reads'
import { createProject, findProjectForWrite } from '../project/mutations'
import { changeProject, projectString } from '../project/scalars'
import { moveSession } from '../session/mutations'
import {
  ensureWorldProjects,
  projectUrl,
  recordSourceMove,
  setProjectParent,
} from './mutations'
import { worldProjectMounts } from './reads'
import type { WorldDoc } from './types'

/** The project is registered before moving documents. If a move is interrupted,
 * another call at the same path resumes it instead of minting another project. */
export async function makeProject(
  repo: Repo,
  worldId: string,
  parentProjectId: string,
  folder: string,
): Promise<string> {
  const path = normalizeDocumentPath(folder)
  if (!path) throw new Error(`${folder} is not a folder path`)
  const world = await repo.find<WorldDoc>(worldId as AnyDocumentId)
  await (world as { whenReady?: () => Promise<void> }).whenReady?.()
  const homeId = world.doc()?.homeProjectId
  const projects = worldProjectMounts(world.doc())
  if (
    parentProjectId !== homeId &&
    !projects.some((p) => p.projectId === parentProjectId)
  )
    throw new Error(`${parentProjectId} is not in this World`)
  const existing = projects.find(
    (p) => p.parentProjectId === parentProjectId && p.folder === path,
  )

  const parent = await findProjectForWrite(repo, parentProjectId)
  const allSources = projectSources(parent.doc()).sources
  if (
    allSources.some(
      (source) =>
        source.folder === path ||
        (source.folder && path.startsWith(`${source.folder}/`)),
    )
  )
    throw new Error(
      'a source at or above this folder cannot yet become a project',
    )
  const sources = allSources.filter((source) =>
    source.folder?.startsWith(`${path}/`),
  )
  const under = Object.entries(parent.doc()?.documents ?? {}).filter(
    ([, ref]) =>
      normalizeDocumentPath(String(ref.path))?.startsWith(`${path}/`),
  )
  const children = projects.filter(
    (p) =>
      p.parentProjectId === parentProjectId && p.folder.startsWith(`${path}/`),
  )
  const folders = projectFolders(parent.doc()).filter(
    (entry) => entry.path === path || entry.path.startsWith(`${path}/`),
  )
  if (
    !under.length &&
    !children.length &&
    !folders.length &&
    !sources.length &&
    !existing
  )
    throw new Error(`no documents, folders, sources or projects under ${path}/`)

  const name = path.split('/').at(-1) as string
  const created = existing ? null : createProject(repo, { title: name })
  // A converted folder starts without a checkout. Make that explicit: an
  // absent sources map on a legacy project infers a default source that might
  // be mounted on another device, and dissolution must not silently drop it.
  created &&
    changeProject(created, (draft) => {
      draft.sources = {}
    })
  const projectId = existing?.projectId ?? created!.documentId
  if (!existing) {
    world.change((draft: WorldDoc) => {
      const entries = ensureWorldProjects(draft)
      entries[projectUrl(projectId)] = {
        displayName: name,
        inference: 'session',
        parent: { projectId: parentProjectId as never, folder: path },
      }
      for (const child of children) {
        const entry = entries[projectUrl(child.projectId)]
        if (entry)
          entry.parent = {
            projectId,
            folder: child.folder.slice(path.length + 1),
          }
      }
    })
  }
  if (under.length) {
    await moveToProject(
      repo,
      parentProjectId,
      projectId,
      {
        folder: path,
        to: '',
      },
      { worldId, expectIds: under.map(([id]) => id) },
    )
  }
  if (folders.length) {
    const child = await findProjectForWrite(repo, projectId)
    changeProject(child, (draft) => {
      if (!draft.folders) draft.folders = {}
      for (const entry of folders) {
        if (entry.path === path) continue // the mount replaces this marker
        draft.folders[entry.id] = { path: entry.path.slice(path.length + 1) }
      }
    })
    changeProject(parent, (draft) => {
      for (const { id } of folders) {
        if (draft.folders?.[id]) delete draft.folders[id]
      }
    })
  }
  if (sources.length) {
    const child = await findProjectForWrite(repo, projectId)
    for (const source of sources) {
      const destination = source.folder?.slice(path.length + 1)
      if (!destination) throw new Error('source has no child folder')
      const present = child.doc()?.sources?.[source.id]
      if (present && projectString(present.folder) !== destination)
        throw new Error(`source ${source.id} already has another folder there`)
    }
    changeProject(child, (draft) => {
      if (!draft.sources) draft.sources = {}
      for (const source of sources) {
        draft.sources[source.id] = {
          ...JSON.parse(JSON.stringify(source)),
          folder: source.folder!.slice(path.length + 1),
        }
      }
      if (!draft.defaultSourceId && sources[0])
        draft.defaultSourceId = sources[0].id
    })
    // Publish the relocation before removing the old catalog entry. A retry
    // after any interruption can still discover the old source and finish it;
    // publishing last could strand an offline device on the old mount forever.
    world.change((draft) => {
      for (const source of sources)
        recordSourceMove(draft, parentProjectId, source.id, projectId)
    })
    changeProject(parent, (draft) => {
      if (!draft.sources) draft.sources = {}
      for (const source of sources) delete draft.sources[source.id]
      if (draft.defaultSourceId && !draft.sources[draft.defaultSourceId]) {
        const remaining = Object.keys(draft.sources)
        if (remaining[0]) draft.defaultSourceId = remaining[0]
        else delete draft.defaultSourceId
      }
    })
  }
  return projectId
}

/** Membership is owned by the sharing layer, not by the World catalog. The
 * caller must fetch the authoritative member list; no evidence means no
 * dissolution. State without a lossless move still refuses. */
export async function dissolveProject(
  repo: Repo,
  worldId: string,
  projectId: string,
  membershipOf: (
    projectId: string,
  ) => Promise<{ actorId: string; memberIds: readonly string[] }>,
): Promise<{
  parentProjectId: string
  folder: string
  moved: string[]
  sessions: string[]
  tasks: string[]
}> {
  if (!membershipOf) throw new Error('project membership cannot be verified')
  const world = await repo.find<WorldDoc>(worldId as AnyDocumentId)
  await (world as { whenReady?: () => Promise<void> }).whenReady?.()
  if (projectId === world.doc()?.homeProjectId)
    throw new Error('the World root cannot be dissolved')
  const mount = worldProjectMounts(world.doc()).find(
    (entry) => entry.projectId === projectId,
  )
  if (!mount?.parentProjectId)
    throw new Error(`${projectId} is not mounted in this World`)
  const parentProjectId = mount.parentProjectId
  const members = await membershipOf(projectId)
  if (
    !members?.actorId ||
    members.memberIds.length !== 1 ||
    members.memberIds[0] !== members.actorId
  )
    throw new Error('a project with another member cannot be dissolved')

  const child = await findProjectForWrite(repo, projectId)
  const parent = await findProjectForWrite(repo, parentProjectId)
  const doc = child.doc()
  if (!doc.sources)
    throw new Error('legacy source ownership is unknown; cannot dissolve')
  const childSources = projectSources(doc).sources
  if (childSources.length !== Object.keys(doc.sources).length)
    throw new Error('project has an unreadable source identity')
  const sourceMoves = childSources.map((source) => ({
    source,
    folder: source.folder ? `${mount.folder}/${source.folder}` : mount.folder,
  }))
  const parentSources = projectSources(parent.doc()).sources
  const otherMounts = worldProjectMounts(world.doc()).filter(
    (entry) =>
      entry.parentProjectId === parentProjectId &&
      entry.projectId !== projectId,
  )
  const nestedMounts = worldProjectMounts(world.doc()).filter(
    (entry) => entry.parentProjectId === projectId,
  )
  const nestedDestinations = nestedMounts.map((entry) => ({
    projectId: entry.projectId,
    folder: `${mount.folder}/${entry.folder}`,
  }))
  const pathsOverlap = (a: string, b: string) =>
    a === b || a.startsWith(`${b}/`) || b.startsWith(`${a}/`)
  for (const [index, nested] of nestedDestinations.entries()) {
    if (
      normalizeDocumentPath(nested.folder) !== nested.folder ||
      otherMounts.some((entry) => pathsOverlap(entry.folder, nested.folder)) ||
      nestedDestinations
        .slice(0, index)
        .some((entry) => pathsOverlap(entry.folder, nested.folder)) ||
      parentSources.some(
        (source) => source.folder && pathsOverlap(source.folder, nested.folder),
      ) ||
      sourceMoves.some((source) =>
        pathsOverlap(source.folder, nested.folder),
      ) ||
      Object.values(parent.doc()?.documents ?? {}).some((ref) =>
        pathsOverlap(String(ref.path), nested.folder),
      ) ||
      Object.values(doc.documents ?? {}).some((ref) =>
        pathsOverlap(`${mount.folder}/${ref.path}`, nested.folder),
      )
    )
      throw new Error(`nested project ${nested.projectId} has no clear landing`)
  }
  for (const [index, { source, folder }] of sourceMoves.entries()) {
    if (normalizeDocumentPath(folder) !== folder)
      throw new Error(`source ${source.id} has an invalid landing folder`)
    const sameId = parentSources.find((other) => other.id === source.id)
    if (
      sameId &&
      JSON.stringify(sameId) !== JSON.stringify({ ...source, folder })
    )
      throw new Error(`source ${source.id} already exists in the parent`)
    const overlaps = (path: string) =>
      path === folder ||
      path.startsWith(`${folder}/`) ||
      folder.startsWith(`${path}/`)
    if (
      parentSources.some(
        (other) =>
          other.id !== source.id && other.folder && overlaps(other.folder),
      ) ||
      sourceMoves.slice(0, index).some((other) => overlaps(other.folder)) ||
      otherMounts.some((other) => overlaps(other.folder)) ||
      Object.values(parent.doc()?.documents ?? {}).some((ref) =>
        overlaps(String(ref.path)),
      ) ||
      Object.values(doc.documents ?? {}).some((ref) =>
        overlaps(`${mount.folder}/${ref.path}`),
      )
    )
      throw new Error(`source ${source.id} overlaps another tree entry`)
  }
  const sessionIds = (doc.sessionIds ?? [])
    .map(projectString)
    .filter((id): id is string => !!id)
  const taskEntries = Object.entries(doc.tasks ?? {})
  if (Object.keys(doc.sessionMeta ?? {}).some((id) => !sessionIds.includes(id)))
    throw new Error('session metadata has no registered session to move')
  for (const [id, entry] of taskEntries) {
    const inParent = parent.doc()?.tasks?.[id]
    if (inParent && JSON.stringify(inParent) !== JSON.stringify(entry))
      throw new Error(`task ${id} already has a different parent entry`)
  }
  for (const [id, entry] of Object.entries(doc.people ?? {})) {
    const inParent = parent.doc()?.people?.[id]
    if (inParent && JSON.stringify(inParent) !== JSON.stringify(entry))
      throw new Error(`person ${id} already has a different parent entry`)
  }
  const documentIds = new Set(Object.keys(doc.documents ?? {}))
  const sessions = new Set(sessionIds)
  if (
    Object.values(
      mapWithConflicts<CommentIndexEntry>(doc, 'commentIndex'),
    ).some(
      (entry) =>
        !(
          (entry.documentId && documentIds.has(String(entry.documentId))) ||
          (entry.sessionId && sessions.has(String(entry.sessionId)))
        ),
    )
  )
    throw new Error('project has comment index entries with no movable owner')
  if (
    Object.keys(doc.apps ?? {}).length ||
    Object.keys(doc.capabilities ?? {}).length ||
    Object.keys(doc.skills ?? {}).length ||
    Object.keys(doc.comments ?? {}).length ||
    Object.keys(doc.pairings ?? {}).length ||
    Object.keys(doc.pairingIndex ?? {}).length ||
    doc.commentsId ||
    doc.versionsDocId ||
    Object.keys(doc.branches ?? {}).length ||
    doc.instructionsDocId ||
    doc.instructions
  )
    throw new Error('this project has metadata that cannot yet move safely')
  if (
    Object.values(parent.doc()?.documents ?? {}).some(
      (ref) => normalizeDocumentPath(String(ref.path)) === mount.folder,
    )
  )
    throw new Error(`${mount.folder} is already a document`)

  const hasContents =
    Object.keys(doc.documents ?? {}).length > 0 ||
    Object.keys(doc.folders ?? {}).length > 0
  const moved = hasContents
    ? (
        await moveToProject(
          repo,
          projectId,
          parentProjectId,
          { all: true, to: mount.folder },
          { worldId, dissolvingProjectId: projectId },
        )
      ).moved
    : []
  if (taskEntries.length) {
    changeProject(parent, (draft) => {
      if (!draft.tasks) draft.tasks = {}
      for (const [id, entry] of taskEntries)
        if (!draft.tasks[id])
          draft.tasks[id] = JSON.parse(JSON.stringify(entry))
    })
  }
  if (Object.keys(doc.people ?? {}).length) {
    changeProject(parent, (draft) => {
      if (!draft.people) draft.people = {}
      for (const [id, entry] of Object.entries(doc.people ?? {}))
        if (!draft.people[id])
          draft.people[id] = JSON.parse(JSON.stringify(entry))
    })
  }
  for (const sessionId of sessionIds)
    await moveSession(repo, sessionId, projectId, parentProjectId)
  if (sourceMoves.length) {
    changeProject(parent, (draft) => {
      ensureProjectSources(draft)
      for (const { source, folder } of sourceMoves)
        if (!draft.sources?.[source.id])
          draft.sources![source.id] = {
            ...JSON.parse(JSON.stringify(source)),
            folder,
          }
      if (!draft.defaultSourceId && doc.defaultSourceId)
        draft.defaultSourceId = doc.defaultSourceId
    })
    world.change((draft) => {
      for (const { source } of sourceMoves)
        recordSourceMove(draft, projectId, source.id, parentProjectId)
    })
    changeProject(child, (draft) => {
      for (const { source } of sourceMoves) delete draft.sources?.[source.id]
      if (draft.defaultSourceId && !draft.sources?.[draft.defaultSourceId])
        delete draft.defaultSourceId
    })
  }
  if (taskEntries.length) {
    changeProject(child, (draft) => {
      for (const [id] of taskEntries)
        if (draft.tasks?.[id]) delete draft.tasks[id]
    })
  }
  if (
    !projectFolders(parent.doc()).some((entry) => entry.path === mount.folder)
  )
    await createFolder(repo, parentProjectId, mount.folder)
  for (const nested of nestedDestinations)
    await setProjectParent(
      repo,
      worldId,
      nested.projectId,
      parentProjectId,
      nested.folder,
    )
  world.change((draft) => {
    const projects = ensureWorldProjects(draft)
    delete projects[projectUrl(projectId)]
  })
  return {
    parentProjectId,
    folder: mount.folder,
    moved,
    sessions: sessionIds,
    tasks: taskEntries.map(([id]) => id),
  }
}
